import React from "react";
import {Checkbox, ControlLabel, FormControl} from "react-bootstrap";
import {FormattedMessage, injectIntl} from "react-intl";
import {connect} from "react-redux";
import {ColumnsDescription, ColumnWidth} from "../common/ui/table/ControlRoomColumns";
import {
  ControlRoomTableProperties,
  ControlRoomTableStateProperties,
  CONTROLROOM_TABLE_HEADER_HEIGHT,
  CONTROLROOM_TABLE_ROW_HEIGHT,
  createBasicTableComponent
} from "../common/ui/table/ControlRoomTable";
import {APPLICATION_APPEND, APPLICATION_OVERWRITE} from "./BatchUpdateDialog";
import ReactDatePicker, {registerLocale} from "react-datepicker";
import {getLanguageAndLocale} from "../common/intl/intl";
import {ar, cs, de, es, fr, he, id, it, ko, nl, pl, pt, ru, tr, zhCN} from 'date-fns/locale'
import {DateUtil} from "../common/util/DateUtil";

registerLocale('ar', ar)
registerLocale('cs', cs)
registerLocale('de', de)
registerLocale('es', es)
registerLocale('fr', fr)
registerLocale('he', he)
registerLocale('id', id)
registerLocale('it', it)
registerLocale('ko', ko)
registerLocale('nl', nl)
registerLocale('pl', pl)
registerLocale('pt', pt)
registerLocale('ru', ru)
registerLocale('tr', tr)
registerLocale('zh', zhCN)

export const listDimensions = (parent: HTMLElement) => {
  return {
    width: parent.clientWidth,
    height: 8 * CONTROLROOM_TABLE_ROW_HEIGHT + CONTROLROOM_TABLE_HEADER_HEIGHT,
  };
};

export interface BatchUpdateProperty {
  name: string;
  wrappers?: string;
  value: string | boolean | string[] | number;
  selected?: boolean;
  messageId: string;
  defaultMessage: string;
  type: BatchPropertyType;
  onChange?: (event: any) => void;
  onToggleSelect?: (event: any) => void;
  onApplicationChange?: (event: any) => void;
  category: string;
  applicationAppendable: boolean;
  application: string;
  possibleValues?: any[];
}

export enum BatchPropertyType {
  STRING,
  BOOL,
  STRINGARRAY,
  DATE,
  LONG,
  DOUBLE,
  ENUM
}

const {locale} = getLanguageAndLocale();
const formatters: { [key: number]: (p: BatchUpdateProperty) => JSX.Element } = {
  [BatchPropertyType.ENUM]: (property) =>
      <FormControl name={property.name}
                   componentClass="select"
                   onChange={property.onChange}
                   disabled={!property.selected}
                   data-cy={"bu-" + property.name + "-value"}
                   className={"batchUpdate"}>
        {property.possibleValues.map((opt, i) => (<option key={opt} selected={i === 0} value={opt}>{opt}</option>))}
      </FormControl>,
  [BatchPropertyType.STRING]: (property) =>
      <FormControl name={property.name}
                   value={property.value as string}
                   onChange={property.onChange}
                   disabled={!property.selected}
                   data-cy={"bu-" + property.name + "-value"}
                   className={"batchUpdate"}/>,
  [BatchPropertyType.STRINGARRAY]: (property) =>
      <FormControl name={property.name}
                   value={property.value as string}
                   onChange={property.onChange}
                   disabled={!property.selected}
                   data-cy={"bu-" + property.name + "-value"}
                   className={"batchUpdate"}/>,
  [BatchPropertyType.BOOL]: (property) =>
      <Checkbox inline={true}
                name={property.name}
                checked={property.value as boolean}
                onChange={property.onChange}
                disabled={!property.selected}
                data-cy={"bu-" + property.name + "-value"}
                className={"batchUpdate"}/>,
  [BatchPropertyType.DATE]: (property) =>
      <ReactDatePicker
          portalId="batchUpdate-datepicker"
          className={"batchUpdate"}
          onChange={(date: any) => {
            property.onChange({target: {name: property.name, value: date}})
          }}
          onMonthChange={(date: any) => {
            property.onChange({target: {name: property.name, value: date}})
          }}
          onYearChange={(date: any) => {
            property.onChange({target: {name: property.name, value: date}})
          }}
          selected={new Date(property.value as string)}
          showMonthDropdown={true}
          showYearDropdown={true}
          scrollableYearDropdown={true}
          showTimeSelect
          locale={locale}
          dateFormat={`${DateUtil.dateFormat()} ${DateUtil.timeFormat()}`}
          timeIntervals={15}
          disabled={!property.selected}
      />,
  [BatchPropertyType.LONG]: (property) =>
      <FormControl name={property.name}
                   value={property.value as number}
                   onChange={property.onChange}
                   disabled={!property.selected}
                   type={"number"}
                   data-cy={"bu-" + property.name + "-value"}
                   onKeyDown={(event) => {
                     if (event.key === ".") {
                       event.preventDefault()
                     }
                   }}
                   onPaste={(event) => {
                     const regex = /^\d*$/;
                     const copiedText = event?.clipboardData?.getData('text')
                     if (copiedText && !regex.test(copiedText)) {
                       event.preventDefault(); //this line executes only if copiedText has all invalid characters
                       return false;
                     }
                   }}
                   className={"batchUpdate"}/>,
  [BatchPropertyType.DOUBLE]: (property) =>
      <FormControl name={property.name}
                   value={property.value as string}
                   onChange={property.onChange}
                   disabled={!property.selected}
                   type={"number"}
                   data-cy={"bu-" + property.name + "-value"}
                   className={"batchUpdate"}/>,
};

const Columns: ColumnsDescription<BatchUpdateProperty> = {
  propertyName: {
    header: <FormattedMessage id="studio.batch-update-properties-list.common-columns.property-name"
                              defaultMessage="Property"/>,
    cellContent(property) {
      return <React.Fragment>
        <Checkbox inline={true}
                  name={property.name}
                  checked={!!property.selected as boolean}
                  onChange={property.onToggleSelect}
                  data-cy={"bu-" + property.name + "-set"}
                  className={"batchUpdate"}
        />
        <ControlLabel>
          {/* tslint:disable-next-line:lcd-no-variables-in-translation-messages */}
          <FormattedMessage id={property.messageId} defaultMessage={property.defaultMessage}/>
        </ControlLabel>
      </React.Fragment>;
    },
    columnWidth: ColumnWidth.flex(2),
  },
  propertyValue: {
    header: <FormattedMessage id="studio.batch-update-properties-list.common-columns.property-value"
                              defaultMessage="Value"/>,
    cellContent(property) {
      return <div onClick={e => e.stopPropagation()}>{formatters[property.type](property)}</div>;
    },
    columnWidth: ColumnWidth.flex(2),
  },
  propertyCategory: {
    header: <FormattedMessage id="studio.batch-update-properties-list.common-columns.property-category"
                              defaultMessage="Category"/>,
    cellContent(property) {
      return <div>{property.category}</div>;
    },
    columnWidth: ColumnWidth.flex(1),
  },
  propertyApplication: {
    header: <FormattedMessage id="studio.batch-update-properties-list.common-columns.property-application"
                              defaultMessage="Application"/>,
    cellContent(property) {
      return <React.Fragment>
        <FormControl componentClass="select"
                     name={property.name}
                     value={property.application}
                     onChange={property.onApplicationChange}
                     data-cy={"bu-" + property.name + "-app"}
                     disabled={!property.selected}
                     className={"batchUpdate"}>
          <option value={APPLICATION_OVERWRITE} key={APPLICATION_OVERWRITE}>{APPLICATION_OVERWRITE}</option>
          {property.applicationAppendable &&
           <option value={APPLICATION_APPEND} key={APPLICATION_APPEND}>{APPLICATION_APPEND}</option>}
        </FormControl> </React.Fragment>;
    },
    columnWidth: ColumnWidth.flex(1),
  }

};

export const BatchUpdatePropertiesListComponent = createBasicTableComponent(Columns, listDimensions);

const mapStateToProps = (state,
                         ownProps: Partial<ControlRoomTableProperties<BatchUpdateProperty>>): ControlRoomTableStateProperties<BatchUpdateProperty> => {
  return {
    data: ownProps.data,
    isPaged: false,
    isAllDataLoaded: true,
  };
};

export const BatchUpdatePropertiesList = connect(mapStateToProps, null)(injectIntl(BatchUpdatePropertiesListComponent));